import { DetailAction } from '../DetailAction';
import { CreateEditRecordingDialog } from '../../../../pages/detail/recording/CreateEditRecordingDialog';
import { SubscriptionSettings, SubscriptionTypes } from './SubscriptionSettings.types';
import {
  DetailTranslationKeys,
  ErrorTranslationKeys,
  PopupTranslationKeys,
} from '../../../../api/models/TranslationKeys';
import { Log } from '@lightningjs/sdk';
import { NpvrSettingsKeys } from '../../../../api/models/NpvrSettingsKeys';
import { Backend } from '../../../../api/Backend';
import { EpgUtil } from '../../../utility/EpgUtil';
export class EpgCreateRecordingAction extends DetailAction {
  private programTitle: string;
  private programId: string;
  private seriesId: string | undefined;
  private channelId: string | undefined;
  private seasonId: string | undefined;
  constructor(
    backend: Backend,
    programTitle: string,
    programId: string,
    seriesId: string | undefined,
    seasonId: string | undefined,
    channelId: string | undefined
  ) {
    super(backend);
    this.programTitle = programTitle;
    this.programId = programId;
    this.seriesId = seriesId;
    this.channelId = channelId;
    this.seasonId = seasonId;
  }

  createSubscriptionOptions(
    type = SubscriptionTypes.PROGRAM,
    beginBuffer?: number,
    endBuffer?: number,
    deleteProtected?: boolean
  ) {
    return new SubscriptionSettings(
      this._backend,
      this._backend.sessionInfo?.npvr?.id,
      type,
      beginBuffer,
      endBuffer,
      deleteProtected,
      !!this.programId && !this.seasonId && !this.seriesId
    );
  }

  override doAction() {
    try {
      let initialType: SubscriptionTypes | undefined = undefined;
      let initialBegin: number | undefined = undefined;
      let initialEnd: number | undefined = undefined;
      let initialProtected: boolean | undefined = undefined;
      let subscriptSettings: SubscriptionSettings | undefined = undefined;
      const loadDataPromise = Promise.all([
        this._backend.cachedNpvrSettings.getSetting(this._backend, NpvrSettingsKeys.recording_episodes),
        this._backend.cachedNpvrSettings.getSetting(this._backend, NpvrSettingsKeys.recording_before_time),
        this._backend.cachedNpvrSettings.getSetting(this._backend, NpvrSettingsKeys.recording_after_time),
        this._backend.cachedNpvrSettings.getSetting(this._backend, NpvrSettingsKeys.recording_delete_protection),
      ])
        .then(res => {
          if (res[0]) {
            initialType = res[0].value as SubscriptionTypes;
          }
          if (res[1]) {
            initialBegin = +res[1].value;
          }
          if (res[2]) {
            initialEnd = +res[2].value;
          }
          if (res[3]) {
            initialProtected = EpgUtil.stringToBoolean(res[3].value);
          }
        })
        .then(() => {
          subscriptSettings = this.createSubscriptionOptions(initialType, initialBegin, initialEnd, initialProtected);
        })
        .catch(() => {
          Log.warn('error fetching recording settings, fallback to defaults');
          subscriptSettings = this.createSubscriptionOptions();
        });

      loadDataPromise.then(() => {
        if (!subscriptSettings) {
          subscriptSettings = this.createSubscriptionOptions();
        }
        this.owner?.fireAncestors('$openDialog', {
          type: CreateEditRecordingDialog,
          backend: this._backend,
          subscriptionSettings: subscriptSettings,
          onSuccess: this.onSettings.bind(this),
          onCancel: undefined,
          buttons: {
            confirm: this._backend.translations.forKey(PopupTranslationKeys.popup_btn_ok),
            cancel: this._backend.translations.forKey(PopupTranslationKeys.popup_btn_cancel),
          },
          title: this._backend.translations.forKey(DetailTranslationKeys.detail_recording_record_popup_title),
          recordingTitle: this.programTitle,
        });
      });
    } catch (e) {
      this.owner?.fireAncestors('$showToast', `[EpgCreateRecordingAction] DoAction() Catch ${e}`, 4000);
    }
  }

  onSettings(settings: SubscriptionSettings) {
    let assetId;
    switch (settings.type) {
      case SubscriptionTypes.PROGRAM:
        assetId = this.programId;
        break;
      case SubscriptionTypes.SERIES:
        assetId = this.seriesId;
        break;
      case SubscriptionTypes.SEASON:
        assetId = this.seasonId;
        break;
      default:
        return;
    }
    if (!this.channelId) return;
    this._backend.cachedRecordingsApi
      .createSubscription(this._backend, settings, assetId!, this.channelId)
      .then(() => {
        Log.info('subscription created');
        this.owner?.fireAncestors('$reloadDetailContent');
      })
      .catch(error => {
        Log.error(error);
        this.owner?.fireAncestors(
          '$showMessage',
          this._backend.translations.forKey(ErrorTranslationKeys.error_recording_start_failed)
        );
      });
  }
}
